<script setup>
import { computed } from 'vue'
import BcsStatusPill from '../common/BcsStatusPill.vue'
import BcsTooltip from '../BcsTooltip.vue'
import BcsDropdownActions from '../common/BcsDropdownActions.vue'
import AlertNotification from '../AlertNotification.vue'
import { useTemplateVariablesStore } from '../../stores/TemplateVariablesStore'
import { useActionCenterStore } from '../../stores/ActionCenterStore'
import { storeToRefs } from 'pinia'

const templateStore = useTemplateVariablesStore()
const { getDefinitionBodySync } = templateStore

const actionCenterStore = useActionCenterStore()
const { shareUpdateEmailSent } = storeToRefs(actionCenterStore)
const { setShareUpdateEmailSent } = actionCenterStore

const props = defineProps({
  employers: { type: Array, required: true },
  sortKey: { type: String, required: true },
  sortAsc: { type: Boolean, required: true },
  summaryStats: { type: Object, required: true },
  taskActions: { type: Array, default: () => [] },
  taskMode: { type: String, default: 'dropdown' },
  summaryOverrides: { type: Array, default: null },
  maskedColumns: { type: Array, default: null },
  hiddenColumns: { type: Array, default: null }
})

function getVisibleActions(row) {
  return props.taskActions.filter(action => action.isVisible(row))
}

function showSummary(column) {
  return !props.summaryOverrides || props.summaryOverrides.includes(column)
}

function isMasked(column) {
  return props.maskedColumns && props.maskedColumns.includes(column)
}

function isHidden(column) {
  return props.hiddenColumns && props.hiddenColumns.includes(column)
}

const hasPastRows = computed(() => props.employers.some(r => r.isPastPlanYear))

const visibleColumnCount = computed(() => {
  const allCols = ['employer', 'riskScore', 'riskDollars', 'actionItems', 'calendarRequirements', 'assessment', 'tasks']
  if (!props.hiddenColumns) return allCols.length
  return allCols.filter(c => !props.hiddenColumns.includes(c)).length
})

const COLUMN_LABELS = {
  riskScore: 'Risk Score',
  riskDollars: 'Risk $$',
  actionItems: 'Action Items',
  calendarRequirements: 'Calendar Requirements',
  assessment: 'Assessment'
}

const hiddenColumnsTooltip = computed(() => {
  if (!props.hiddenColumns || !props.hiddenColumns.length) return ''
  const labels = props.hiddenColumns.map(c => COLUMN_LABELS[c] || c)
  return `Hidden Columns:<br>${labels.join(' & ')}`
})

const emits = defineEmits(['toggle-sort', 'task-clicked', 'assessment-clicked'])

function onHeaderClick(key) {
  emits('toggle-sort', key)
}

const sortIcon = computed(() => (key) => {
  if (props.sortKey !== key) return 'bi-chevron-expand'
  return props.sortAsc ? 'bi-chevron-down' : 'bi-chevron-up'
})

function getRiskScoreColor(row) {
  if (!row.hasCompleteAssessment || row.riskPercentLabel === '--') return ''
  const score = row.riskPercent
  if (score >= 75) return 'text-success'
  if (score >= 50) return 'text-warning-500'
  return 'text-danger'
}

function getRiskDollarColor(row) {
  return getRiskScoreColor(row)
}

function onTaskAction(row, actionKey) {
  emits('task-clicked', { row, action: actionKey })
}

function onTaskButtonClick(row) {
  const actions = getVisibleActions(row)
  if (actions.length === 1) {
    emits('task-clicked', { row, action: actions[0].key })
  }
}
</script>

<template>
  <div v-if="shareUpdateEmailSent" class="mb-3">
    <AlertNotification
      :showAlert="shareUpdateEmailSent"
      @update:showAlert="setShareUpdateEmailSent"
      type-alert="success"
    >
      <strong>Update shared successfully!</strong>
    </AlertNotification>
  </div>

  <!-- Hidden columns indicator -->
  <div v-if="hiddenColumns && hiddenColumns.length" class="text-end mb-1">
    <BcsTooltip
      :simpleText="hiddenColumnsTooltip"
      :show-arrow="true"
      :contentClass="'simple-text-tooltip'"
    >
      <span class="th-definition hand-cursor text-16"><med>{{ hiddenColumns.length }} hidden column{{ hiddenColumns.length > 1 ? 's' : '' }}</med></span>
    </BcsTooltip>
  </div>

  <table class="table table-sm align-middle mb-2" id="baseBookOverviewTable">
    <thead>
      <tr>
        <th style="width: 18%">
          <button
            class="btn btn-link text-decoration-none text-16 th-btn"
            type="button"
            @click="onHeaderClick('employer')"
          >
            <strong class="text-secondary">Employer</strong>
            <i class="bi ms-1" :class="sortIcon('employer')" />
          </button>
        </th>
        <th v-if="!isHidden('riskScore')" style="width: 10%">
          <button
            class="btn btn-link text-decoration-none text-16 th-btn"
            type="button"
            @click="onHeaderClick('riskPercent')"
          >
            <BcsTooltip
              :simpleText="getDefinitionBodySync('bookDetailsRiskScoreColumnTooltip')"
              :show-arrow="true"
              :contentClass="'simple-text-tooltip'"
            >
              <strong class="th-definition">Risk Score</strong>
            </BcsTooltip>
            <i class="bi ms-1" :class="sortIcon('riskPercent')" />
          </button>
        </th>
        <th v-if="!isHidden('riskDollars')" style="width: 10%">
          <button
            class="btn btn-link text-decoration-none text-16 th-btn"
            type="button"
            @click="onHeaderClick('riskDollars')"
          >
            <BcsTooltip
              :simpleText="getDefinitionBodySync('bookDetailsRiskDollarsColumnTooltip')"
              :show-arrow="true"
              :contentClass="'simple-text-tooltip'"
            >
              <strong class="th-definition">Risk $$</strong>
            </BcsTooltip>
            <i class="bi ms-1" :class="sortIcon('riskDollars')" />
          </button>
        </th>
        <th v-if="!isHidden('actionItems')" style="width: 15%">
          <button
            class="btn btn-link text-decoration-none text-16 th-btn"
            type="button"
            @click="onHeaderClick('actionItems')"
          >
            <BcsTooltip
              :simpleText="getDefinitionBodySync('bookDetailsActionItemsColumnTooltip')"
              :show-arrow="true"
              :contentClass="'simple-text-tooltip'"
            >
              <strong class="th-definition">Action Items</strong>
            </BcsTooltip>
            <i class="bi ms-1" :class="sortIcon('actionItems')" />
          </button>
        </th>
        <th v-if="!isHidden('calendarRequirements')" style="width: 18%">
          <button
            class="btn btn-link text-decoration-none text-16 th-btn"
            type="button"
            @click="onHeaderClick('calendarRequirements')"
          >
            <BcsTooltip
              :simpleText="getDefinitionBodySync('bookDetailsCalendarReqColumnTooltip')"
              :show-arrow="true"
              :contentClass="'simple-text-tooltip'"
            >
              <strong class="th-definition">Calendar Requirements</strong>
            </BcsTooltip>
            <i class="bi ms-1" :class="sortIcon('calendarRequirements')" />
          </button>
        </th>
        <th v-if="!isHidden('assessment')" style="width: 18%">
          <button
            class="btn btn-link text-decoration-none text-16 th-btn"
            type="button"
            @click="onHeaderClick('assessment')"
          >
            <BcsTooltip
              :simpleText="getDefinitionBodySync('bookDetailsAssessmentColumnTooltip')"
              :show-arrow="true"
              :contentClass="'simple-text-tooltip'"
            >
              <strong class="th-definition">Assessment</strong>
            </BcsTooltip>
            <i class="bi ms-1" :class="sortIcon('assessment')" />
          </button>
        </th>
        <th style="width: 6%">
          <div class="text-secondary text-16 th-btn">
          <BcsTooltip
            :simpleText="getDefinitionBodySync('bookDetailsTasksColumnTooltip')"
            :show-arrow="true"
            :contentClass="'simple-text-tooltip'"
          >
            <span class="th-definition"><strong>Tasks</strong></span>
          </BcsTooltip>
          </div>
        </th>
      </tr>
      <!-- Summary row -->
      <tr class="summary-row">
        <td>
          <BcsStatusPill v-if="showSummary('employer')" :text="`${summaryStats.employerCount} Employers`" color="primary" content-class="text-14" />
        </td>
        <td v-if="!isHidden('riskScore')">
          <BcsStatusPill v-if="showSummary('riskScore')" :text="summaryStats.avgRiskLabel" :color="summaryStats.avgRiskColor" content-class="text-14" />
        </td>
        <td v-if="!isHidden('riskDollars')">
          <BcsStatusPill v-if="showSummary('riskDollars')" :text="summaryStats.totalRiskDollarsLabel" color="primary" content-class="text-14" />
        </td>
        <td v-if="!isHidden('actionItems')">
          <BcsStatusPill v-if="showSummary('actionItems')" :text="`${summaryStats.actionItemsNeedAttention} Need Action`" :color="summaryStats.actionItemsColor" content-class="text-14" />
        </td>
        <td v-if="!isHidden('calendarRequirements')">
          <BcsStatusPill v-if="showSummary('calendarRequirements')" :text="`${summaryStats.calendarReqNeedReminders} Need Reminders`" :color="summaryStats.calendarReqColor" content-class="text-14" />
        </td>
        <td v-if="!isHidden('assessment')">
          <BcsStatusPill v-if="showSummary('assessment')" :text="`${summaryStats.assessmentsNeedCompletion} Need Completion`" :color="summaryStats.assessmentColor" content-class="text-14" />
        </td>
        <td></td>
      </tr>
    </thead>
    <tbody id="baseBookOverviewTableBody">
      <tr
        v-for="row in employers"
        :key="row.id"
        :id="`allEmployerRow-${row.id}`"
        :class="{ 'row-past': row.isPastPlanYear }"
      >
        <td class="cell-employer text-16">
          <div class="d-flex align-items-center gap-2">
            <BcsStatusPill
              v-if="row.isPastPlanYear"
              text="Past"
              color="muted"
              :tooltip-text="`Plan Year Start Date: ${row.planStartDate}`"
              content-class="text-12"
            />
            <span v-else-if="hasPastRows" class="past-pill-spacer"></span>
            <span class="text-truncate">{{ row.employerName || '--' }}</span>
          </div>
        </td>
        <td v-if="!isHidden('riskScore')" class="text-16" :class="!isMasked('riskScore') ? getRiskScoreColor(row) : ''">
          <strong>{{ isMasked('riskScore') ? '--' : row.riskPercentLabel }}</strong>
        </td>
        <td v-if="!isHidden('riskDollars')" class="text-16" :class="!isMasked('riskDollars') ? getRiskDollarColor(row) : ''">
          <strong>{{ isMasked('riskDollars') ? '--' : row.riskDollarsLabel }}</strong>
        </td>
        <td v-if="!isHidden('actionItems')" class="text-16">{{ isMasked('actionItems') ? '--' : row.actionItemsLabel }}</td>
        <td v-if="!isHidden('calendarRequirements')" class="text-16" :class="{ 'text-gray-500': row.isPastPlanYear }">{{ isMasked('calendarRequirements') ? '--' : row.calendarRequirementsLabel }}</td>
        <td v-if="!isHidden('assessment')" class="text-16">
          <BcsTooltip
            v-if="row.assessmentTooltip"
            :simpleText="row.assessmentTooltip"
            :show-arrow="true"
            :contentClass="'simple-text-tooltip'"
            placement="left"
          >
            <span class="assessment-link hand-cursor" @click="emits('assessment-clicked', row)">{{ row.assessmentStatus }}</span>
          </BcsTooltip>
          <span v-else>{{ row.assessmentStatus }}</span>
        </td>
        <td class="text-16 tasks-cell">
          <!-- Dropdown mode -->
          <template v-if="taskMode === 'dropdown' && getVisibleActions(row).length">
            <BcsDropdownActions
              :item-id="row.id"
              :actions="getVisibleActions(row)"
              mode="teleport"
              @action-click="onTaskAction(row, $event)"
            />
          </template>
          <!-- Single button mode (e.g. Incomplete Assessments) -->
          <template v-else-if="taskMode === 'button' && getVisibleActions(row).length">
            <BcsTooltip
              v-if="row.assessmentTooltip"
              :simpleText="row.assessmentTooltip"
              :show-arrow="true"
              :contentClass="'simple-text-tooltip'"
              placement="left"
            >
              <button
                class="btn btn-primary px-2 py-1"
                type="button"
                @click="onTaskButtonClick(row)"
              >
                <i class="bi bi-chevron-right text-white"></i>
              </button>
            </BcsTooltip>
            <button
              v-else
              class="btn btn-primary px-2 py-1"
              type="button"
              @click="onTaskButtonClick(row)"
            >
              <i class="bi bi-chevron-right text-white"></i>
            </button>
          </template>
          <!-- Multiple buttons mode (e.g. Incomplete Action Items) -->
          <template v-else-if="taskMode === 'buttons'">
            <div class="d-flex gap-1 justify-content-start flex-nowrap">
              <button
                v-for="action in getVisibleActions(row)"
                :key="action.key"
                class="btn btn-sm text-nowrap"
                :class="action.variant || 'btn-outline-primary'"
                type="button"
                @click="emits('task-clicked', { row, action: action.key })"
              >
                <i v-if="action.icon" class="bi me-1" :class="action.icon"></i>{{ action.label }}
              </button>
            </div>
          </template>
        </td>
      </tr>
      <tr v-if="!employers.length">
        <td :colspan="visibleColumnCount" class="text-center py-4 text-gray-600">No results</td>
      </tr>
    </tbody>
  </table>

</template>

<style scoped lang="scss">
.table {
  border-collapse: collapse;
  border: 1px solid #E5E7EB;
}

.table thead th {
  border: none;
  border-bottom: 1px solid #E5E7EB;
  padding: 0;
}

.table tbody td {
  border: none;
  border-bottom: 1px solid #E5E7EB;
  padding: 0.6rem 1rem;
}

.table tbody tr:last-child td {
  border-bottom: none;
}

.th-btn {
  padding: 0.6rem 1rem;
  white-space: nowrap;
}

.th-definition {
  color: var(--bs-primary);
  cursor: pointer;
  text-decoration: none;
  border-bottom: 3px dotted var(--bs-primary);
}

.th-label {
  padding: 0.6rem 1rem;
  display: inline-block;
}

.cell-employer {
  padding: 0.6rem 1.2rem;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Summary row
.summary-row td {
  padding: 0.5rem 1rem;
  background-color: var(--bs-info);
}

// Tasks column
.tasks-cell {
  text-align: center;
}

.row-past td {
  background-color: #f4f4f5;
}

.past-pill-spacer {
  display: inline-block;
  width: 58px;
  flex-shrink: 0;
}

.text-warning-500 {
  color: var(--warning-500) !important;
}

.assessment-link {
  color: var(--bs-primary);
}
</style>

